import type { RefObject } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, MessageCircleIcon } from 'lucide-react';
import { AccountAfterCheckout } from '../account/AccountAfterCheckout';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { useCatalog } from '../../contexts/CatalogContext';
import { isPhysical } from '../../core/rules/stock';
import { ease } from '../../core/format/motion';
import { shopLabels } from '../../data/shopLabels';
import { euroCents } from './checkoutRules';
import { OrderSourceAsk } from './OrderSourceAsk';
import type { PlacedOrder } from './useCheckout';
type Props = {
  placed: PlacedOrder;
  headingRef: RefObject<HTMLHeadingElement>;
  /** Uit `?t=` van de terugkeerlink (voor "Hoe kwam je bij ons?"); leeg = de sessie van de order. */
  token?: string;
};
const quietLink = 'inline-flex items-center gap-2 py-2 text-[14px] text-ink underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay hover:underline';

/**
 * De bedankpagina als één rustige kolom, van boven naar beneden gelezen:
 * 1. de bevestiging;
 * 2. direct daaronder de éne volgende stap (je bestelling volgen, of een account maken),
 *    als het zwaarste blok op de pagina, zodat hij nooit meer wegvalt in een zijkolom;
 * 3. wat er daarna gebeurt, als korte tijdlijn;
 * 4. een vraag stellen of verder winkelen, als rustige links.
 */
export function OrderPlaced({
  placed,
  headingRef,
  token = ''
}: Props) {
  const reduce = useReducedMotion();
  const {
    productById
  } = useCatalog();
  const products = placed.accountOrder.lines.map(l => productById(l.productId));
  // Niets op de post (alleen cadeaubonnen en/of een kuur): geen bezorgdatum en geen inpakstap beloven.
  const nothingToShip = products.length > 0 && products.every(p => p !== undefined && !isPhysical(p));
  const hasKuur = products.some(p => p?.kind === 'kuur');
  const hasGiftCard = products.some(p => p?.kind === 'giftcard');
  const delivery = !nothingToShip ? `${policyCopy.delivery} (op werkdagen)` : hasKuur && hasGiftCard ? 'Cadeaubon per mail, kuur in Mijn omgeving' : hasKuur ? 'Je kuur staat in Mijn omgeving' : 'Per mail, direct na betaling';
  const secondStep = !nothingToShip ? {
    when: 'Bij het inpakken',
    text: `${founder.firstName} pakt je bestelling zelf in, met een handgeschreven gebruiksadvies.`
  } : hasKuur ? {
    when: 'Daarna',
    text: 'Plan je eerste sessie wanneer het je uitkomt. Hij is al betaald; je ziet in Mijn omgeving hoeveel sessies je nog hebt.'
  } : {
    when: 'Daarna',
    text: 'De cadeaubon komt in een aparte mail, klaar om door te sturen of te printen.'
  };
  const steps = [{
    when: 'Vandaag',
    text: `Je bevestiging gaat naar ${placed.email}. Niet gezien? Kijk even bij spam.`
  }, secondStep, {
    when: policyCopy.followUp,
    text: placed.remind ? 'Een appje om te horen hoe je huid reageert. Later volgt een seintje voordat je flesje op is.' : 'Een appje om te horen hoe je huid reageert. Echt, geen automatische mail.'
  }];
  return <section className="bg-sand" data-id="element-7046" data-mp="16g73q1dwhurh">
      <div className="mx-auto max-w-3xl px-6 pb-24 pt-12 lg:px-10 lg:pb-28 lg:pt-20" data-id="element-7047" data-mp="sjbizboaeevl">
        <motion.div initial={reduce ? false : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease
      }} data-id="element-7048" data-mp="xhmqhfr40dan">
          {/* Het ene moment dat een klein, stevig accent verdient: de bestelling is binnen. */}
          <motion.span initial={reduce ? false : {
          opacity: 0,
          scale: 0.85
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 420,
          damping: 24,
          delay: 0.08
        }} className="flex h-12 w-12 items-center justify-center rounded-full bg-ink" aria-hidden="true" data-id="element-7049" data-mp="nkgxg1yxqc7s">
            <CheckIcon className="h-5 w-5 text-shell" strokeWidth={1.75} data-id="element-7050" data-mp="4t7l7kxs59ci" />
          </motion.span>
          <h1 ref={headingRef} tabIndex={-1} className="mt-7 font-display text-[2.6rem] font-light leading-[1.05] text-ink sm:text-6xl" data-id="element-7051" data-mp="176ly899n7hph">
            Dank je wel, {placed.firstName}.
          </h1>
          <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-7052" data-mp="yebp6vahhi9t">
            We hebben je bestelling ontvangen en {founder.firstName} gaat ermee aan de slag.
          </p>
          {/* Drie feiten om terug te lezen, in plaats van één lange zin. */}
          <dl className="mt-7 grid max-w-xl gap-x-6 gap-y-3 border-t border-line pt-5 text-[15px] sm:grid-cols-[8.5rem_1fr]" data-id="element-7053" data-mp="ohff4l4xso35">
            <dt className="text-muted" data-id="element-7054" data-mp="jjdsxrc7s8ng">Bestelling</dt>
            <dd className={placed.orderNumber ? 'font-medium tabular-nums text-ink' : 'text-muted'} data-id="element-7055" data-mp="fg8wa5etf16q">{placed.orderNumber ?? shopLabels.orderNumberLater}</dd>
            <dt className="text-muted" data-id="element-7056" data-mp="29t732f1b3fpb">Betaald</dt>
            <dd className="tabular-nums text-ink" data-id="element-7057" data-mp="29sypekxd06d2">{euroCents(placed.total)}</dd>
            <dt className="text-muted" data-id="element-7058" data-mp="24vfdhro4w5oa">{nothingToShip ? 'Ontvangen' : 'Bezorging'}</dt>
            <dd className="text-ink" data-id="element-7059" data-mp="2ceomhjsqa2ct">{delivery}</dd>
            {placed.gift && <>
                <dt className="text-muted" data-id="element-7060" data-mp="2hc7y0by1k112">Cadeau erbij</dt>
                <dd className="text-ink" data-id="element-7061" data-mp="2han50qrdkq5b">{placed.gift.split(' + ').join(', ')}</dd>
              </>}
          </dl>
        </motion.div>

        {/* De volgende stap staat direct onder de bevestiging: op mobiel én desktop het eerste wat je daarna ziet. */}
        <div className="mt-10" data-id="element-7062" data-mp="1tglseqlbpf85">
          <AccountAfterCheckout email={placed.email} firstName={placed.firstName} orderId={placed.orderId} order={placed.accountOrder} subscribeIds={placed.subscribeIds} data-id="element-7063" data-mp="1thdjpp7w0qsm" />
        </div>

        <div className="mt-16" data-id="element-7064" data-mp="27flebqhgbg0m">
          <h2 className="font-display text-2xl font-light text-ink" data-id="element-7065" data-mp="27ch3495kjk0g">Zo gaat het verder</h2>
          {/* Volgorde telt hier: dit gebeurt na elkaar. Op desktop naast elkaar, op mobiel onder elkaar. */}
          <ol className="relative mt-6 space-y-7 border-l border-line pl-7 sm:grid sm:grid-cols-3 sm:gap-8 sm:space-y-0 sm:border-l-0 sm:border-t sm:pl-0 sm:pt-6" data-id="element-7066" data-mp="3ivtks3007bhk">
            {steps.map((s, i) => <li key={s.when} className="relative" data-id="element-7067" data-mp="1esgtdofdh4va" data-mpi={i}>
                <span aria-hidden="true" className={`absolute -left-[33px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-sand sm:-top-[31px] sm:left-0 ${i === 0 ? 'bg-clay' : 'bg-line'}`} data-id="element-7068" data-mp="1solzja3fm90o" />
                <p className="text-[14px] text-clay" data-id="element-7069" data-mp="zyvias6i6r0c">{s.when}</p>
                <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-7070" data-mp="w0xwlzbxyn1p">{s.text}</p>
              </li>)}
          </ol>
        </div>

        {/* Ná betalen en onder alles wat ertoe doet: nooit een drempel, één tik. */}
        <OrderSourceAsk orderId={placed.orderId} token={token} ask={placed.askSource} data-id="element-7071" data-mp="14yhwb3u5oba7" />

        <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-2 border-t border-line pt-6" data-id="element-7072" data-mp="h8nkmplkosrj">
          <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={quietLink} data-id="element-7073" data-mp="c9koxvrhfyw8">
            <MessageCircleIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7074" data-mp="2nxpldjschu9g" />
            Een vraag? App {founder.firstName}
            <span className="sr-only" data-id="element-7075" data-mp="2eyudm39nkc41">{shopLabels.viaWhatsApp}</span>
          </a>
          <Link to="/webshop" className={quietLink} data-id="element-7076" data-mp="2jxf6tiqhlviz">
            Verder winkelen
          </Link>
        </div>
      </div>
    </section>;
}